import { del, get, post, patch, apiRequestWithHeaders } from "./client";

const PROGENY_BASE_URL = "/admin/progeny";

// ============================================================================
// Types
// ============================================================================

export type ProgenyEnvironment = "PROD" | "UAT" | "DEV" | "unknown";

/** Lowercase environment key for switching endpoints. */
export type ProgenyEnvKey = "prod" | "uat" | "dev";

/** Environment configuration info. */
export interface ProgenyEnvironmentInfo {
  name: string;
  base_url: string;
  is_active: boolean;
}

/** Response from environment get/set endpoints. */
export interface ProgenyEnvironmentResponse {
  current_environment: ProgenyEnvKey;
  environments: Record<string, ProgenyEnvironmentInfo>;
}

/**
 * Admin connection health — aligns with GET /api/v1/admin/progeny/status.
 * `environment` is included when the backend returns it.
 */
export interface ProgenyConnectionStatus {
  connected: boolean;
  environment?: ProgenyEnvironment;
  base_url: string;
  enabled: boolean;
  oauth_configured: boolean;
  oauth_token_ok: boolean;
  oauth_error?: string | null;
  webhook_secret_configured: boolean;
  latency_ms?: number | null;
  error?: string | null;
}

export interface ProgenyConfig {
  enabled: boolean;
  base_url: string;
  oauth_configured: boolean;
  tenant_id_configured: boolean;
  webhook_secret_configured: boolean;
  timeout_seconds: number;
  webhook_url: string;
  auto_process_webhooks: boolean;
}

/** Count of inbound webhooks grouped by `source`. */
export interface SourceCount {
  source: string;
  count: number;
}

/**
 * Aggregated dashboard metrics — GET /api/v1/admin/progeny/stats
 * (see FE hookup: `ProgenyProcessingStats`).
 */
export interface ProgenyStats {
  total_webhooks_received: number;
  total_reviews_processed: number;
  successful: number;
  failed: number;
  in_progress: number;
  avg_processing_seconds: number | null;
  webhooks_last_24h: number;
  processing_runs_last_24h: number;
  processing_completed_last_24h: number;
  processing_failed_last_24h: number;
  webhooks_by_source: SourceCount[];
  last_webhook_at: string | null;
  last_success_at: string | null;
  last_failure_at: string | null;
}

/** Alias for spec / docs */
export type ProgenyProcessingStats = ProgenyStats;

/**
 * Typical webhook row statuses from the API audit log (not exhaustive).
 * Examples: `received`, `pending_approval`, `queued`, `processing`, `completed`, `failed`.
 */
export type WebhookEventStatus = string;

/**
 * Single webhook audit row — GET /api/v1/admin/progeny/webhooks
 */
export interface WebhookEventItem {
  event_id: string;
  event_type: string;
  review_id: string | null;
  account_id: string | null;
  enriched_at?: string | null;
  enriched_by?: string | null;
  enrichment?: WebhookEventEnrichment | null;
  task_id: string | null;
  check_type: string | null;
  correlation_id: string | null;
  source: string | null;
  metadata: Record<string, unknown>;
  received_at: string;
  status: string;
  error: string | null;
  /** Set after ops approves a pending compliance-case webhook (ISO datetime). */
  approved_at?: string | null;
  /** Approver email when `approved_at` is set. */
  approved_by?: string | null;
  /** Background task id after approval (`run_progeny_compliance_case`) — aligns with task manager. */
  pipeline_task_id?: string | null;
  /** Saved case document discovery snapshot (Sam testing). */
  document_pack?: CaseDocumentDiscoveryResult | null;
  pack_saved_at?: string | null;
  pack_saved_by?: string | null;
  pack_approved_at?: string | null;
  pack_approved_by?: string | null;
  /** Set when the approved pack has been queued for case-checking. */
  case_sent_at?: string | null;
  case_sent_by?: string | null;
  case_id?: string | null;
  /**
   * Raw HTTP / JSON string as stored (public inbound UTF-8 decode, or admin trigger `json.dumps`).
   * Older rows may omit this until new traffic is ingested.
   */
  raw_body?: string | null;
  /** True when `raw_body` was shortened (see `PROGENY_WEBHOOK_RAW_BODY_MAX_CHARS` on the API). */
  raw_body_truncated?: boolean | null;
  /** Character length of the original body before truncation; meaningful when `raw_body_truncated`. */
  raw_body_original_length?: number | null;
}

/** @deprecated Prefer `WebhookEventItem` */
export type WebhookEvent = WebhookEventItem;

export interface WebhookEventsResponse {
  total: number;
  events: WebhookEventItem[];
}

export interface WebhookQueryParams {
  limit?: number;
  offset?: number;
  event_type?: string;
  /** Alias for `status` on the API (row status filter). */
  status?: string;
  status_filter?: string;
  source?: string;
  correlation_id?: string;
  review_id?: string;
  account_id?: string;
  task_id?: string;
}

export type ProcessingLogStatus = "running" | "completed" | "failed";

/**
 * Pipeline run row — GET /api/v1/admin/progeny/processing
 */
export interface ProcessingLogItem {
  log_id: string;
  event_type: string | null;
  review_id: string | null;
  account_id: string | null;
  task_id: string | null;
  check_type: string | null;
  correlation_id: string | null;
  metadata: Record<string, unknown>;
  needs_human_review: boolean | null;
  human_review_reasons: string[];
  file_note_document_ids: string[];
  content_available_for_file_note: boolean;
  document_content_fetch_attempted: boolean;
  document_content_fetch_ok: boolean;
  started_at: string;
  completed_at: string | null;
  status: string;
  documents_fetched: number;
  result_posted: boolean;
  error: string | null;
  duration_seconds: number | null;
}

export interface ProcessingLogDetailResponse extends ProcessingLogItem {
  context: Record<string, unknown> | null;
}

/** @deprecated Prefer `ProcessingLogItem` */
export type ProcessingLog = ProcessingLogItem;

export interface ProcessingLogsResponse {
  total: number;
  logs: ProcessingLogItem[];
}

export interface ProcessingQueryParams {
  limit?: number;
  offset?: number;
  review_id?: string;
  account_id?: string;
  task_id?: string;
  event_type?: string;
  correlation_id?: string;
  status?: string;
}

export interface ProgenyConfigUpdatePayload {
  auto_process_webhooks?: boolean;
  notification_email?: string;
  notes?: string;
}

/**
 * POST /api/v1/admin/progeny/trigger — provide `review_id` **or** (`account_id` + `task_id`).
 */
export interface ProgenyTriggerRequest {
  review_id?: string;
  account_id?: string;
  task_id?: string;
  check_type?: "check_1" | "check_2";
  correlation_id?: string;
  force?: boolean;
}

export interface ProgenyTriggerResponse {
  status: "accepted" | "rejected";
  review_id?: string;
  correlation_id?: string;
  /** Present when the pipeline run was accepted; use for support / status correlation. */
  task_id?: string;
  message: string;
}

/**
 * POST /api/v1/admin/progeny/checks/trigger — queue worker processing (RabbitMQ) like the public checks trigger.
 * Use either `review_id` or both `account_id` and `task_id`.
 */
export interface ProgenyAdminChecksTriggerRequest {
  review_id?: string;
  account_id?: string;
  task_id?: string;
  check_type?: string;
  correlation_id?: string;
  event_type?: string;
}

/**
 * 202 response from admin checks trigger — queues ani-worker `process_progeny_inbound`.
 */
export interface ProgenyAdminChecksTriggerResponse {
  status: string;
  review_id?: string;
  correlation_id?: string;
  /** Background task / audit id for support and log correlation. */
  task_id?: string;
}

export interface ProgenyReAuthResult {
  success: boolean;
  token_url: string | null;
  expires_in_seconds: number | null;
  triggered_by: string | null;
  error: string | null;
  connection_status: ProgenyConnectionStatus;
}

// ============================================================================
// User-facing Types
// ============================================================================

export interface ProgenyContact {
  ContactId: string;
  FirstName?: string;
  LastName?: string;
  FullName?: string;
  Email?: string;
  Phone?: string;
  ParentAccountId?: string;
  DateOfBirth?: string;
  AddressLine1?: string;
  City?: string;
  PostalCode?: string;
}

export interface ProgenyAccount {
  AccountId: string;
  Name?: string;
  Email?: string;
  Phone?: string;
  PrimaryContactId?: string;
  AccountType?: number;
  AccountTypeLabel?: string;
}

export interface ProgenyDocument {
  DocumentId: string;
  Subject?: string;
  Description?: string;
  FileName?: string;
  MimeType?: string;
  FileSize?: number;
  DocumentUrl?: string;
  ExternalUrl?: string;
  ExternalUrlExpires?: string;
  DocumentTypeLabel?: string;
  SourceLabel?: string;
  DocumentDate?: string;
  CreatedOn?: string;
  ModifiedOn?: string;
  ContactId?: string;
  AccountId?: string;
  IsActive?: boolean;
  /** ID of the parent entity (Review, Contact, Account, or Recommendation) */
  RegardingObjectId?: string;
  /** Whether this document can be synced (has valid ExternalUrl) */
  Syncable?: boolean;
}

export interface DocumentListResponse {
  documents: ProgenyDocument[];
  total: number;
  /** Count of documents that can be synced (have valid ExternalUrl) */
  syncable_count?: number;
}

/**
 * Options for listing documents by contact or account.
 */
export interface DocumentListOptions {
  /** Maximum number of documents to return */
  top?: number;
  /** If true, only return documents that can be synced */
  syncableOnly?: boolean;
}

export interface ProgenyDocumentSyncRequest {
  contact_id?: string;
  account_id?: string;
  generate_sharing_links: boolean;
}

export interface SyncedDocument {
  document_id: string;
  filename: string;
  file_size: number;
  /** External document id from Progeny (legacy responses may use `curo_document_id`). */
  progeny_document_id: string;
  /** @deprecated Prefer `progeny_document_id`; retained for transitional API responses. */
  curo_document_id?: string;
  azure_url: string;
}

export interface ProgenyDocumentSyncResult {
  total_found: number;
  synced: number;
  skipped: number;
  failed: number;
  errors: string[];
  documents: SyncedDocument[];
}

export interface ProgenyReview {
  ReviewId: string;
  Name?: string;
  Review?: string;
  ContactId?: string;
  ContactName?: string;
  AccountId?: string;
  AccountName?: string;
  AdviserId?: string;
  AdviserName?: string;
  ReviewReasonLabel?: string;
  ScheduledReviewDate?: string;
  BookedForDate?: string;
  ReviewStartedDate?: string;
  ReviewCompletedDate?: string;
  StatusCodeLabel?: string;
  IsActive?: boolean;
}

export interface ProgenyPlan {
  PlanId: string;
  Name?: string;
  ContactId?: string;
  AccountId?: string;
  PlanTypeLabel?: string;
  StatusCodeLabel?: string;
  StartDate?: string;
  EndDate?: string;
  IsActive?: boolean;
  CreatedOn?: string;
  ModifiedOn?: string;
}

export interface ProgenyRecommendation {
  RecommendationId: string;
  Name?: string;
  ContactId?: string;
  AccountId?: string;
  RecommendationTypeLabel?: string;
  StatusCodeLabel?: string;
  AdviserId?: string;
  AdviserName?: string;
  IsActive?: boolean;
  CreatedOn?: string;
  ModifiedOn?: string;
}

export interface ProgenyAttitudeToRisk {
  AttitudeToRiskId: string;
  Name?: string;
  ContactId?: string;
  AccountId?: string;
  RiskProfileName?: string;
  RiskProfileNarrative?: string;
  AtrScore?: number;
  AtrToolUsedLabel?: string;
  RiskProfileToolUsedLabel?: string;
  RiskCategoryLabel?: string;
  AssessmentDate?: string;
  NextReviewDate?: string;
  CapacityForLoss?: string | null;
  CapacityForLossDescription?: string | null;
  CorroborativeExperience?: boolean;
  ChangesMadeToProfile?: boolean;
  IsActive?: boolean;
  CreatedOn?: string;
  ModifiedOn?: string;
}

export interface ProgenyVulnerability {
  VulnerabilityId: string;
  Name?: string;
  ContactId?: string;
  AccountId?: string;
  CategoryLabel?: string;
  TypeLabel?: string;
  CurrentStatusLabel?: string;
  VulnerabilityStatusLabel?: string;
  IdentificationSourceLabel?: string;
  ConsentObtainedLabel?: string;
  FurtherDetails?: string;
  DateIdentified?: string;
  DateReviewed?: string;
  AdjustmentsRequired?: boolean;
  ReferredToSpecialist?: boolean;
  IsActive?: boolean;
  CreatedOn?: string;
  ModifiedOn?: string;
}

/**
 * Planning Objective — uses raw Dataverse t4a_ prefixed column names.
 * Fields with `@OData.Community.Display.V1.FormattedValue` suffixes
 * provide human-readable labels; the FE should prefer the formatted
 * value and fall back to the raw numeric value.
 */
export interface ProgenyPlanningObjective {
  t4a_planningobjectiveid: string;
  t4a_name?: string;
  t4a_objective?: string;
  t4a_objectivetype?: number;
  "t4a_objectivetype@OData.Community.Display.V1.FormattedValue"?: string;
  t4a_clientnotes?: string;
  t4a_objectivenotes?: string;
  t4a_ownership?: number;
  "t4a_ownership@OData.Community.Display.V1.FormattedValue"?: string;
  t4a_dateobjectiveset?: string;
  t4a_datesatisfied?: string | null;
  statuscode?: number;
  "statuscode@OData.Community.Display.V1.FormattedValue"?: string;
  _t4a_contact_value?: string;
  /** Raw Dataverse fields with `t4a_` prefix and OData formatted-value keys. */
  [key: string]: unknown;
}

export interface AdminContactDebugResponse {
  contact_id: string;
  data: ProgenyContact;
}

export interface AdminDocumentsDebugResponse {
  contact_id: string;
  document_count: number;
  documents: ProgenyDocument[];
}

export interface ProgenyReviewDocuments {
  review_id: string;
  client_id: string;
  document_count: number;
  documents: Record<string, unknown>[];
}

/**
 * Review context — resolves all related entity IDs from a single review.
 */
export interface ProgenyReviewContext {
  reviewId: string;
  reviewName: string;
  accountId: string;
  accountName: string;
  primaryContactId: string;
  householdFactfindId: string;
  trustFactfindId: string;
  corporateFactfindId: string;
}

/**
 * Document filter info returned from /reviews/{id}/full endpoint.
 */
export interface DocumentFilterInfo {
  /** Type of filter applied */
  filter_type: "review_id" | "none";
  /** The review ID used for filtering (if filter_type is "review_id") */
  review_id?: string;
  /** Total documents for the account before filtering */
  original_count: number;
  /** Documents after filtering to this review */
  filtered_count: number;
}

/**
 * Full review response — one-call compliance data aggregation.
 * Fans out concurrently to fetch review, account, contact, docs, vulnerabilities, and factfind.
 */
export interface ProgenyFullReviewContext {
  context: ProgenyReviewContext;
  review: ProgenyReview;
  account: ProgenyAccount | null;
  contact: ProgenyContact | null;
  documents: ProgenyDocument[];
  document_count: number;
  /** Info about document filtering applied */
  document_filter_applied?: DocumentFilterInfo;
  vulnerabilities: Record<string, unknown>[];
  factfind_type: "household" | "trust" | "corporate" | null;
  factfind: Record<string, unknown> | null;
}

/**
 * Query params for paginated meeting notes.
 */
export interface MeetingNotesParams {
  page_size?: number;
  page?: number;
  since?: string;
  is_client_meeting?: boolean;
}

/** A single SaturnAI meeting note. */
export interface MeetingNote {
  [key: string]: unknown;
}

/** Paginated meeting notes response. */
export interface MeetingNotesResponse {
  notes: MeetingNote[];
  total: number;
  page: number;
  page_size: number;
}

/** Response from latest client meeting note endpoint. */
export interface LatestMeetingNoteResponse {
  /** Meeting ID (BE returns as Id PascalCase) */
  Id?: string;
  id?: string;
  /** Meeting date in ISO 8601 format (BE may return as MeetingDate or meetingDate) */
  MeetingDate?: string;
  meetingDate?: string;
  accountId?: string;
  AccountId?: string;
  crmAccountId?: string;
  CrmAccountId?: string;
  subject?: string;
  Subject?: string;
  /** Meeting name (BE returns as Name) */
  Name?: string;
  name?: string;
  participants?: string[];
  Participants?: string[];
  summary?: string;
  Summary?: string;
  [key: string]: unknown;
}

/** A single transcript segment from Saturn API. */
export interface TranscriptSegment {
  id?: string;
  timestamp?: number;
  speaker?: string;
  text?: string;
}

/** Saturn transcript data structure (nested in response). */
export interface SaturnTranscriptData {
  meeting_id?: string;
  file_url?: string;
  file_type?: string;
  transcript_segments?: TranscriptSegment[];
}

/** Meeting transcript response. */
export interface MeetingTranscriptResponse {
  /** Saturn API returns data in a nested structure */
  data?: SaturnTranscriptData;
  /** Meeting ID (BE may return as MeetingId or meetingId) */
  MeetingId?: string;
  meetingId?: string;
  /** Transcript text (BE may return as Transcript or transcript) */
  Transcript?: string;
  transcript?: string;
  participants?: string[];
  Participants?: string[];
  duration_seconds?: number;
  DurationSeconds?: number;
  audio_url?: string;
  AudioUrl?: string;
  video_url?: string;
  VideoUrl?: string;
  created_at?: string;
  CreatedAt?: string;
  [key: string]: unknown;
}

/** Options for fetching meeting transcript. */
export interface MeetingTranscriptOptions {
  includeAudio?: boolean;
  includeVideo?: boolean;
}

// ============================================================================
// Document Classification Types
// ============================================================================

/**
 * Document category for compliance classification.
 * Maps to folder structure in SharePoint and compliance requirements.
 */
export type DocumentCategory =
  | "aml"
  | "compliance"
  | "advice"
  | "client_agreement"
  | "correspondence"
  | "factfind"
  | "research"
  | "provider"
  | "legal"
  | "meeting"
  | "submission"
  | "mortgage"
  | "death_claim"
  | "group_schemes"
  | "file_check"
  | "other";

/**
 * Classification metadata for a Progeny document.
 * Provides category assignment, folder mapping, and compliance status.
 */
export interface DocumentClassification {
  /** Primary category of the document */
  category: DocumentCategory;
  /** Matched document type from taxonomy (e.g., "SmartSearch", "Passport/ID Card") */
  matched_type: string | null;
  /** Expected SharePoint folder path */
  expected_folder: string | null;
  /** Expected subfolder within the main folder */
  expected_subfolder: string | null;
  /** Whether this document is required for compliance */
  required_for_compliance: boolean;
  /** Whether the filename follows naming conventions */
  naming_valid: boolean;
  /** Warnings about naming convention issues */
  naming_warnings: string[];
}

/**
 * A Progeny document with classification metadata attached.
 */
export interface ClassifiedDocument extends ProgenyDocument {
  classification: DocumentClassification;
}

/**
 * Result of parsing/validating a document filename against naming conventions.
 */
export interface NamingValidation {
  /** Original filename */
  original_name: string;
  /** Whether the naming follows conventions */
  is_valid: boolean;
  /** Identified document type from filename */
  document_type: string | null;
  /** Parsed date from filename (DD.MM.YYYY format) */
  parsed_date: string | null;
  /** Client title (Mr, Mrs, etc.) */
  client_title: string | null;
  /** Client initial */
  client_initial: string | null;
  /** Client surname */
  client_surname: string | null;
  /** Provider name if present */
  provider: string | null;
  /** Non-critical naming issues */
  warnings: string[];
  /** Critical naming errors */
  errors: string[];
}

/**
 * Request payload for classifying documents.
 */
export interface ClassifyDocumentsRequest {
  documents: ProgenyDocument[];
}

/**
 * Response from document classification endpoint.
 */
export interface ClassifyDocumentsResponse {
  classified_documents: ClassifiedDocument[];
  summary: {
    total: number;
    by_category: Record<DocumentCategory, number>;
    compliance_required_found: number;
    compliance_required_missing: number;
    naming_issues: number;
  };
}

/**
 * Compliance checklist item for a required document.
 */
export interface ComplianceChecklistItem {
  /** Required document type name */
  document_type: string;
  /** Whether this document is required for the review type */
  required: boolean;
  /** Whether a matching document was found */
  found: boolean;
  /** The matched document if found */
  document?: ClassifiedDocument;
  /** Warning message if there are issues */
  warning?: string;
}

/**
 * Review type for determining compliance requirements.
 */
export type ReviewType = "annual" | "adhoc_itp" | "adhoc_itt";

/** A SmartSearch subject record as returned by the SmartSearch API. */
export interface SmartSearchSubject {
  type: string;
  id: string;
  attributes: {
    name: string;
    group_type: string;
    client_reference: string;
    monitoring_status: string;
    num_outstanding: number;
    num_true_matches: number;
    num_matches: number;
    subject_category: string[];
    document_last_updated_at: string | null;
    follower_names: string[] | null;
  };
  meta: {
    updated_at: string;
    has_providers: boolean;
  };
  relationships?: Record<string, unknown>;
}

/** Envelope returned by the SmartSearch search endpoint. */
interface SmartSearchResponse {
  Status: string;
  StatusCode: number;
  ErrorMessage: string;
  Data: {
    data: SmartSearchSubject[];
    meta: {
      page: {
        total_items: number;
        items_per_page: number;
        number_of_pages: number;
        current: number;
      };
    };
  };
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get connection health status
 */
export async function getStatus(): Promise<ProgenyConnectionStatus> {
  return get<ProgenyConnectionStatus>(`${PROGENY_BASE_URL}/status`);
}

/**
 * Get current Progeny configuration
 */
export async function getConfig(): Promise<ProgenyConfig> {
  return get<ProgenyConfig>(`${PROGENY_BASE_URL}/config`);
}

/**
 * Get processing statistics
 */
export async function getStats(): Promise<ProgenyStats> {
  return get<ProgenyStats>(`${PROGENY_BASE_URL}/stats`);
}

/**
 * Update operational config settings (MongoDB-stored only)
 */
export async function updateConfig(payload: ProgenyConfigUpdatePayload): Promise<ProgenyConfig> {
  return patch<ProgenyConfig>(`${PROGENY_BASE_URL}/config`, payload);
}

/**
 * Test the connection to Progeny API
 */
export async function testConnection(): Promise<ProgenyConnectionStatus> {
  return post<ProgenyConnectionStatus>(`${PROGENY_BASE_URL}/test-connection`);
}

/**
 * Clear cached OAuth token and acquire a fresh one from Entra ID.
 * Returns updated connection status so the UI can reflect the new state.
 */
export async function reAuth(): Promise<ProgenyReAuthResult> {
  return post<ProgenyReAuthResult>(`${PROGENY_BASE_URL}/re-auth`);
}

/**
 * Get current Progeny environment configuration.
 */
export async function getProgenyEnvironment(): Promise<ProgenyEnvironmentResponse> {
  return get<ProgenyEnvironmentResponse>(`${PROGENY_BASE_URL}/environment`);
}

/**
 * Switch Progeny environment (prod, uat, dev).
 * Resets the backend client to use the selected environment's credentials.
 */
export async function setProgenyEnvironment(
  environment: ProgenyEnvKey
): Promise<ProgenyEnvironmentResponse> {
  return post<ProgenyEnvironmentResponse>(`${PROGENY_BASE_URL}/environment`, {
    environment,
  });
}

/** Row in case document discovery lists */
export interface CaseDocumentRow {
  document_id: string;
  file_name: string;
  folder_path: string;
  folder_name: string;
  effective_date: string | null;
  effective_date_field: string | null;
  list_section: "review_folder" | "outside_since_review_start" | "compliance_folder" | "adhoc_review_folder" | "sharepoint_direct_link";
  has_download_url: boolean;
}

/** One review option for case document discovery */
export interface AccountReviewOption {
  review_id: string;
  label: string;
  status: string | null;
  booked_for_iso: string | null;
  start_date_iso: string | null;
  end_date_iso: string | null;
  is_active: boolean | null;
  is_selected: boolean;
}

/** CRM snapshot on an enriched webhook row */
export interface WebhookEventEnrichment {
  account_id?: string | null;
  account_name?: string | null;
  primary_contact_name?: string | null;
  account?: Record<string, unknown>;
  reviews?: AccountReviewOption[];
  review_count?: number;
}

/** GET /admin/progeny/case-documents/reviews */
export interface AccountReviewsListResponse {
  account_id: string;
  reviews: AccountReviewOption[];
}

/** POST /admin/progeny/case-documents/discover */
export interface CaseDocumentDiscoveryResult {
  account_id: string;
  selected_review_id?: string | null;
  account_reviews?: AccountReviewOption[];
  review_selection: {
    pick_reason?: string;
    project_key?: string | null;
    review?: Record<string, unknown>;
  };
  review_folder: Record<string, unknown>;
  review_period_start_iso: string | null;
  files_in_review_folder: CaseDocumentRow[];
  files_in_compliance_folders?: CaseDocumentRow[];
  compliance_folder_count?: number;
  files_outside_folder_since_review_start: CaseDocumentRow[];
  recommended_count: number;
  recommended_document_ids: string[];
  folders_seen: Record<string, unknown>[];
}

export function formatReviewPickerDate(iso: string | null | undefined): string {
  if (!iso?.trim()) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function reviewStartIso(review: ProgenyReview & Record<string, unknown>): string | null {
  for (const key of ["ReviewStartedDate", "reviewStartedDate", "BookedForDate", "bookedForDate", "ScheduledReviewDate", "scheduledReviewDate"] as const) {
    const v = typeof review[key] === "string" ? review[key].trim() : "";
    if (v) return v;
  }
  return null;
}

function reviewIdOf(review: ProgenyReview & Record<string, unknown>): string {
  return String(review.ReviewId ?? review.reviewId ?? "").trim();
}

function reviewStr(review: ProgenyReview & Record<string, unknown>, pascal: string, camel: string): string {
  const v = review[pascal as keyof typeof review] ?? review[camel as keyof typeof review];
  return typeof v === "string" ? v.trim() : "";
}

function formatReviewActive(isActive: boolean | undefined): string {
  if (isActive === true) return "Yes";
  if (isActive === false) return "No";
  return "—";
}

/** Map Progeny review rows to case-review picker options (matches API review_options_for_account). */
export function progenyReviewsToAccountOptions(
  reviews: ProgenyReview[],
  selectedReviewId?: string | null,
): AccountReviewOption[] {
  const sel = (selectedReviewId || "").trim().toLowerCase();
  const options: AccountReviewOption[] = [];
  for (const review of reviews) {
    const row = review as ProgenyReview & Record<string, unknown>;
    const reviewId = reviewIdOf(row);
    if (!reviewId) continue;
    const reason = reviewStr(row, "ReviewReasonLabel", "reviewReasonLabel");
    const status = reviewStr(row, "StatusCodeLabel", "statusCodeLabel");
    const startIso = reviewStartIso(row);
    const endIso = reviewStr(row, "ReviewCompletedDate", "reviewCompletedDate") || null;
    const start = formatReviewPickerDate(startIso);
    const end = formatReviewPickerDate(endIso);
    const isActive = row.IsActive ?? row.isActive;
    const active = formatReviewActive(typeof isActive === "boolean" ? isActive : undefined);
    const title =
      reason ||
      reviewStr(row, "Name", "name") ||
      reviewStr(row, "Review", "review").slice(0, 48) ||
      reviewId.slice(0, 8);
    const label = `${title} | ${start} → ${end} | active: ${active}${status ? ` | ${status}` : ""}`;
    options.push({
      review_id: reviewId,
      label,
      status: status || null,
      booked_for_iso:
        reviewStr(row, "BookedForDate", "bookedForDate") ||
        reviewStr(row, "ScheduledReviewDate", "scheduledReviewDate") ||
        null,
      start_date_iso: startIso,
      end_date_iso: endIso,
      is_active: typeof isActive === "boolean" ? isActive : null,
      is_selected: sel ? reviewId.toLowerCase() === sel : false,
    });
  }
  return options;
}

function applySelectedReview(
  res: AccountReviewsListResponse,
  selectedReviewId?: string | null,
): AccountReviewsListResponse {
  const sel = (selectedReviewId || "").trim().toLowerCase();
  if (!sel) return res;
  return {
    ...res,
    reviews: res.reviews.map((r) => ({
      ...r,
      is_selected: r.review_id.toLowerCase() === sel,
    })),
  };
}

/** List reviews for an account (picker). Primary: case-documents/reviews; fallback: raw by-account. */
export async function listCaseAccountReviews(
  accountId: string,
  selectedReviewId?: string | null,
): Promise<AccountReviewsListResponse> {
  const aid = accountId.trim();
  const query = new URLSearchParams({ account_id: aid });

  const res = await get<AccountReviewsListResponse>(`${PROGENY_BASE_URL}/case-documents/reviews?${query}`);
  const formatted = Array.isArray(res?.reviews) ? res.reviews : [];
  if (formatted.length > 0) {
    return applySelectedReview({ account_id: res.account_id || aid, reviews: formatted }, selectedReviewId);
  }

  const raw = await getReviewsByAccount(aid, 200);
  const mapped = progenyReviewsToAccountOptions(Array.isArray(raw) ? raw : [], selectedReviewId);
  return { account_id: aid, reviews: mapped };
}

/**
 * Discover case documents: review SharePoint folder + uploads since review start.
 */
export async function discoverCaseDocuments(
  accountId: string,
  reviewId?: string | null
): Promise<CaseDocumentDiscoveryResult> {
  const query = new URLSearchParams({ account_id: accountId });
  if (reviewId?.trim()) query.set("review_id", reviewId.trim());
  return post<CaseDocumentDiscoveryResult>(
    `${PROGENY_BASE_URL}/case-documents/discover?${query.toString()}`,
    {}
  );
}

/**
 * Get paginated webhook events
 */
export async function getWebhooks(params?: WebhookQueryParams): Promise<WebhookEventsResponse> {
  const query = new URLSearchParams();
  if (params?.limit != null) query.set("limit", String(params.limit));
  if (params?.offset != null) query.set("offset", String(params.offset));
  if (params?.event_type) query.set("event_type", params.event_type);
  const statusFilter = params?.status ?? params?.status_filter;
  if (statusFilter) query.set("status", statusFilter);
  if (params?.source) query.set("source", params.source);
  if (params?.correlation_id) query.set("correlation_id", params.correlation_id);
  if (params?.review_id) query.set("review_id", params.review_id);
  if (params?.account_id) query.set("account_id", params.account_id);
  if (params?.task_id) query.set("task_id", params.task_id);
  const qs = query.toString();
  return get<WebhookEventsResponse>(`${PROGENY_BASE_URL}/webhooks${qs ? `?${qs}` : ""}`);
}

/**
 * Single webhook audit row by MongoDB ObjectId (24 hex chars).
 */
export async function getWebhookById(eventId: string): Promise<WebhookEventItem> {
  return get<WebhookEventItem>(`${PROGENY_BASE_URL}/webhooks/${eventId}`);
}

export interface WebhookEnrichBatchResponse {
  enriched: number;
  skipped: number;
  failed: number;
  errors: string[];
}

export interface WebhookDeleteBatchResponse {
  deleted: number;
  not_found: number;
}

/** Pull account/contact/reviews from Progeny and persist on the webhook row. */
export async function enrichWebhookEvent(eventId: string): Promise<WebhookEventItem> {
  return post<WebhookEventItem>(`${PROGENY_BASE_URL}/webhooks/${eventId}/enrich`, {});
}

/** Enrich up to `limit` webhook rows that are not enriched yet. */
export async function enrichUnenrichedWebhooks(
  limit = 25
): Promise<WebhookEnrichBatchResponse> {
  return post<WebhookEnrichBatchResponse>(
    `${PROGENY_BASE_URL}/webhooks/enrich-unenriched?limit=${limit}`,
    {}
  );
}

/** Delete one webhook audit row. */
export async function deleteWebhookEvent(eventId: string): Promise<void> {
  await del(`${PROGENY_BASE_URL}/webhooks/${eventId}`);
}

/** Delete multiple webhook audit rows (test cleanup). */
export async function deleteWebhookEventsBatch(
  eventIds: string[]
): Promise<WebhookDeleteBatchResponse> {
  return post<WebhookDeleteBatchResponse>(`${PROGENY_BASE_URL}/webhooks/delete-batch`, {
    event_ids: eventIds,
  });
}

/** Discover and persist document pack on a webhook row (re-runs discovery server-side). */
export async function saveWebhookDocumentPack(
  eventId: string,
  reviewId?: string | null
): Promise<WebhookEventItem> {
  const body: { review_id?: string } = {};
  if (reviewId?.trim()) body.review_id = reviewId.trim();
  return post<WebhookEventItem>(
    `${PROGENY_BASE_URL}/webhooks/${eventId}/save-document-pack`,
    body
  );
}

/** Approve the saved document pack on a webhook row. */
export async function approveWebhookDocumentPack(eventId: string): Promise<WebhookEventItem> {
  return post<WebhookEventItem>(
    `${PROGENY_BASE_URL}/webhooks/${eventId}/approve-document-pack`,
    {}
  );
}

/** 202 response from send-to-case. */
export interface SendPackToCaseResponse {
  status: string;
  event_id: string;
  task_id: string;
  pipeline: string;
}

/** Queue ``run_progeny_compliance_case`` for an approved document pack. */
export async function sendWebhookPackToCase(eventId: string): Promise<SendPackToCaseResponse> {
  return post<SendPackToCaseResponse>(
    `${PROGENY_BASE_URL}/webhooks/${eventId}/send-to-case`,
    {}
  );
}

/**
 * Get paginated processing logs
 */
export async function getProcessingLogs(
  params?: ProcessingQueryParams
): Promise<ProcessingLogsResponse> {
  const query = new URLSearchParams();
  if (params?.limit != null) query.set("limit", String(params.limit));
  if (params?.offset != null) query.set("offset", String(params.offset));
  if (params?.review_id) query.set("review_id", params.review_id);
  if (params?.account_id) query.set("account_id", params.account_id);
  if (params?.task_id) query.set("task_id", params.task_id);
  if (params?.event_type) query.set("event_type", params.event_type);
  if (params?.correlation_id) query.set("correlation_id", params.correlation_id);
  if (params?.status) query.set("status", params.status);
  const qs = query.toString();
  return get<ProcessingLogsResponse>(`${PROGENY_BASE_URL}/processing${qs ? `?${qs}` : ""}`);
}

/**
 * One pipeline run by MongoDB ObjectId, including CRM context when present.
 */
export async function getProcessingLogById(
  logId: string
): Promise<ProcessingLogDetailResponse> {
  return get<ProcessingLogDetailResponse>(`${PROGENY_BASE_URL}/processing/${logId}`);
}

/**
 * Manually trigger processing for a review
 */
export async function triggerReview(payload: ProgenyTriggerRequest): Promise<ProgenyTriggerResponse> {
  return post<ProgenyTriggerResponse>(`${PROGENY_BASE_URL}/trigger`, payload);
}

/**
 * Queue a Progeny compliance check through the worker + RabbitMQ path (production-like).
 *
 * @param payload - `review_id` or (`account_id` + `task_id`); optional `check_type`, `correlation_id`, `event_type`.
 * @returns 202 body with `task_id` for support when accepted.
 */
export async function queueAdminProgenyChecksTrigger(
  payload: ProgenyAdminChecksTriggerRequest
): Promise<ProgenyAdminChecksTriggerResponse> {
  return post<ProgenyAdminChecksTriggerResponse>(
    `${PROGENY_BASE_URL}/checks/trigger`,
    payload
  );
}

/**
 * Debug: get raw review context from Progeny API via admin endpoint
 */
export async function getAdminReviewContext(
  reviewId: string
): Promise<Record<string, unknown>> {
  return get<Record<string, unknown>>(`${PROGENY_BASE_URL}/review/${reviewId}/context`);
}

/**
 * Get review documents from Progeny API (for debugging)
 */
export async function getReviewDocuments(reviewId: string): Promise<ProgenyReviewDocuments> {
  return get<ProgenyReviewDocuments>(`${PROGENY_BASE_URL}/review/${reviewId}/documents`);
}

// ============================================================================
// User-facing API Functions
// ============================================================================

/**
 * Fetch a raw contact record from Progeny by Dataverse GUID
 */
export async function getContact(contactId: string): Promise<ProgenyContact> {
  return get<ProgenyContact>(`${PROGENY_BASE_URL}/contacts/${contactId}`);
}

/**
 * Fetch a raw account record from Progeny by Dataverse GUID
 */
export async function getAccount(accountId: string): Promise<ProgenyAccount> {
  return get<ProgenyAccount>(`${PROGENY_BASE_URL}/accounts/${accountId}`);
}

/**
 * List documents from Progeny for a given contact.
 *
 * @param contactId - The contact GUID
 * @param options - Optional configuration
 * @param options.top - Maximum number of documents to return
 * @param options.syncableOnly - If true, only return syncable documents
 */
export async function getDocumentsByContact(
  contactId: string,
  options?: DocumentListOptions
): Promise<DocumentListResponse> {
  const query = new URLSearchParams({ contact_id: contactId });
  if (options?.top != null) query.set("top", String(options.top));
  if (options?.syncableOnly) query.set("syncable_only", "true");
  return get<DocumentListResponse>(`${PROGENY_BASE_URL}/documents/by-contact?${query}`);
}

/**
 * List documents from Progeny for a given account.
 *
 * @param accountId - The account GUID
 * @param options - Optional configuration
 * @param options.top - Maximum number of documents to return
 * @param options.syncableOnly - If true, only return syncable documents
 */
export async function getDocumentsByAccount(
  accountId: string,
  options?: DocumentListOptions
): Promise<DocumentListResponse> {
  const query = new URLSearchParams({ account_id: accountId });
  if (options?.top != null) query.set("top", String(options.top));
  if (options?.syncableOnly) query.set("syncable_only", "true");
  return get<DocumentListResponse>(`${PROGENY_BASE_URL}/documents/by-account?${query}`);
}

/**
 * Sync documents from Progeny into our Azure blob storage and MongoDB.
 * After sync, docs are available via /api/v1/documents/ with metadata.source="progeny".
 */
export async function syncDocuments(
  payload: ProgenyDocumentSyncRequest
): Promise<ProgenyDocumentSyncResult> {
  return post<ProgenyDocumentSyncResult>(`${PROGENY_BASE_URL}/documents/sync`, payload);
}

/**
 * Resolve all related entity IDs from a single review.
 */
export async function getReviewContext(reviewId: string): Promise<ProgenyReviewContext> {
  return get<ProgenyReviewContext>(`${PROGENY_BASE_URL}/reviews/${reviewId}/context`);
}

/**
 * Get full details of a single review.
 */
export async function getReviewDetail(reviewId: string): Promise<ProgenyReview> {
  return get<ProgenyReview>(`${PROGENY_BASE_URL}/reviews/${reviewId}`);
}

/**
 * Options for fetching full review context.
 */
export interface GetFullReviewOptions {
  /**
   * Whether to filter documents to only those linked to this review.
   * - true (default): Only documents with RegardingObjectId matching this review
   * - false: All documents for the account
   */
  filterDocsByReview?: boolean;
}

/**
 * One-call compliance data aggregation for a review.
 * Fans out concurrently to fetch review, account, contact, docs,
 * vulnerabilities, and factfind.
 *
 * @param reviewId - The review GUID
 * @param options - Optional configuration
 * @param options.filterDocsByReview - If false, returns all account docs (default: true)
 */
export async function getFullReview(
  reviewId: string,
  options?: GetFullReviewOptions
): Promise<ProgenyFullReviewContext> {
  const query = new URLSearchParams();
  if (options?.filterDocsByReview === false) {
    query.set("filter_docs_by_review", "false");
  }
  const qs = query.toString();
  return get<ProgenyFullReviewContext>(
    `${PROGENY_BASE_URL}/reviews/${reviewId}/full${qs ? `?${qs}` : ""}`
  );
}

/**
 * List reviews from Progeny for a given contact
 */
export async function getReviewsByContact(
  contactId: string,
  top?: number
): Promise<ProgenyReview[]> {
  const query = new URLSearchParams({ contact_id: contactId });
  if (top != null) query.set("top", String(top));
  return get<ProgenyReview[]>(`${PROGENY_BASE_URL}/reviews/by-contact?${query}`);
}

/**
 * List reviews from Progeny for a given account
 */
export async function getReviewsByAccount(
  accountId: string,
  top?: number
): Promise<ProgenyReview[]> {
  const query = new URLSearchParams({ account_id: accountId });
  if (top != null) query.set("top", String(top));
  return get<ProgenyReview[]>(`${PROGENY_BASE_URL}/reviews/by-account?${query}`);
}

/**
 * Get a household factfind by ID.
 */
export async function getHouseholdFactfind(
  factfindId: string
): Promise<Record<string, unknown>> {
  return get<Record<string, unknown>>(
    `${PROGENY_BASE_URL}/factfinds/household/${factfindId}`
  );
}

/**
 * Get a trust factfind by ID.
 */
export async function getTrustFactfind(
  factfindId: string
): Promise<Record<string, unknown>> {
  return get<Record<string, unknown>>(
    `${PROGENY_BASE_URL}/factfinds/trust/${factfindId}`
  );
}

/**
 * Get a corporate factfind by ID.
 */
export async function getCorporateFactfind(
  factfindId: string
): Promise<Record<string, unknown>> {
  return get<Record<string, unknown>>(
    `${PROGENY_BASE_URL}/factfinds/corporate/${factfindId}`
  );
}

/**
 * List corporate factfinds for an account.
 */
export async function getCorporateFactfindsByAccount(
  accountId: string,
  top?: number
): Promise<Record<string, unknown>[]> {
  const query = new URLSearchParams({ account_id: accountId });
  if (top != null) query.set("top", String(top));
  return get<Record<string, unknown>[]>(
    `${PROGENY_BASE_URL}/factfinds/corporate/by-account?${query}`
  );
}

/**
 * Get paginated SaturnAI meeting notes.
 */
export async function getMeetingNotes(
  params?: MeetingNotesParams
): Promise<MeetingNotesResponse> {
  const query = new URLSearchParams();
  if (params?.page_size != null) query.set("page_size", String(params.page_size));
  if (params?.page != null) query.set("page", String(params.page));
  if (params?.since) query.set("since", params.since);
  if (params?.is_client_meeting != null)
    query.set("is_client_meeting", String(params.is_client_meeting));
  const qs = query.toString();
  return get<MeetingNotesResponse>(
    `${PROGENY_BASE_URL}/meeting-notes${qs ? `?${qs}` : ""}`
  );
}

/**
 * Download a meeting note document by document ID (legacy path-based endpoint).
 */
export async function getMeetingNoteDocument(
  documentId: string
): Promise<Blob> {
  return get<Blob>(`${PROGENY_BASE_URL}/meeting-notes/document/${documentId}`);
}

/**
 * Get the URL for downloading a Saturn meeting note document.
 * Note: This URL requires authentication - use downloadSaturnMeetingNoteDocument() for actual downloads.
 */
export function getSaturnMeetingNoteDocumentUrl(meetingId: string): string {
  return `${PROGENY_BASE_URL}/saturn/meeting-notes/document?id=${encodeURIComponent(meetingId)}`;
}

/**
 * Download a Saturn meeting note document by meeting ID.
 * Uses authenticated API client to fetch the document as a blob and trigger browser download.
 *
 * @param meetingId - The Saturn meeting UUID
 * @param filename - Optional filename (defaults to meeting-note-{id}.docx)
 */
export async function downloadSaturnMeetingNoteDocument(
  meetingId: string,
  filename?: string
): Promise<void> {
  const url = getSaturnMeetingNoteDocumentUrl(meetingId);
  const response = await apiRequestWithHeaders<Blob>({
    method: "GET",
    url,
    responseType: "blob",
    headers: {
      Accept: "application/octet-stream",
    },
  });

  let finalFilename = filename || `meeting-note-${meetingId}.docx`;

  if (!filename) {
    const contentDisposition =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"] ||
      response.headers["CONTENT-DISPOSITION"];

    if (contentDisposition) {
      let filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i);
      if (!filenameMatch) {
        filenameMatch = contentDisposition.match(/filename=['"]([^'"]+)['"]/i);
      }
      if (!filenameMatch) {
        filenameMatch = contentDisposition.match(/filename=([^;,\s]+)/i);
      }
      if (filenameMatch && filenameMatch[1]) {
        finalFilename = filenameMatch[1].trim().replace(/^['"]|['"]$/g, "");
      }
    }
  }

  const blob = response.data;
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = finalFilename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(blobUrl);
  document.body.removeChild(a);
}

/**
 * Get the latest client meeting note for a specific CRM account.
 * Returns 404 if no meeting note found.
 */
export async function getLatestClientMeetingNote(
  accountId: string
): Promise<LatestMeetingNoteResponse> {
  const query = new URLSearchParams({ account_id: accountId });
  return get<LatestMeetingNoteResponse>(
    `${PROGENY_BASE_URL}/saturn/meeting-notes-client-latest?${query}`
  );
}

/**
 * Get meeting transcript from Saturn Shuttle API.
 * Includes optional audio/video file references.
 */
export async function getMeetingTranscript(
  meetingId: string,
  options?: MeetingTranscriptOptions
): Promise<MeetingTranscriptResponse> {
  const query = new URLSearchParams({ meeting_id: meetingId });
  if (options?.includeAudio != null) {
    query.set("include_audio", String(options.includeAudio));
  }
  if (options?.includeVideo != null) {
    query.set("include_video", String(options.includeVideo));
  }
  return get<MeetingTranscriptResponse>(
    `${PROGENY_BASE_URL}/saturn/meeting/transcript?${query}`
  );
}

/**
 * Search SmartSearch subjects by name (min 2 chars).
 * The backend returns a `{ Status, Data: { data: [...] } }` envelope;
 * we unwrap it to return just the subject array.
 */
export async function searchSmartSearchSubjects(
  name: string
): Promise<SmartSearchSubject[]> {
  const query = new URLSearchParams({ name });
  const raw = await get<SmartSearchResponse>(
    `${PROGENY_BASE_URL}/smartsearch/subjects?${query}`
  );
  if (Array.isArray(raw)) return raw as unknown as SmartSearchSubject[];
  return raw?.Data?.data ?? [];
}

/**
 * List financial plans from Progeny for a given contact
 */
export async function getPlansByContact(
  contactId: string,
  top?: number
): Promise<ProgenyPlan[]> {
  const query = new URLSearchParams({ contact_id: contactId });
  if (top != null) query.set("top", String(top));
  return get<ProgenyPlan[]>(`${PROGENY_BASE_URL}/plans/by-contact?${query}`);
}

/**
 * List recommendations from Progeny for a given contact
 */
export async function getRecommendationsByContact(
  contactId: string,
  top?: number
): Promise<ProgenyRecommendation[]> {
  const query = new URLSearchParams({ contact_id: contactId });
  if (top != null) query.set("top", String(top));
  return get<ProgenyRecommendation[]>(`${PROGENY_BASE_URL}/recommendations/by-contact?${query}`);
}

/**
 * List attitude-to-risk assessments from Progeny for a given contact
 */
export async function getAttitudeToRiskByContact(
  contactId: string,
  top?: number
): Promise<ProgenyAttitudeToRisk[]> {
  const query = new URLSearchParams({ contact_id: contactId });
  if (top != null) query.set("top", String(top));
  return get<ProgenyAttitudeToRisk[]>(`${PROGENY_BASE_URL}/attitude-to-risk/by-contact?${query}`);
}

/**
 * List vulnerabilities from Progeny for a given contact
 */
export async function getVulnerabilitiesByContact(
  contactId: string,
  top?: number
): Promise<ProgenyVulnerability[]> {
  const query = new URLSearchParams({ contact_id: contactId });
  if (top != null) query.set("top", String(top));
  return get<ProgenyVulnerability[]>(`${PROGENY_BASE_URL}/vulnerabilities/by-contact?${query}`);
}

/**
 * List planning objectives from Progeny for a given contact
 */
export async function getPlanningObjectivesByContact(
  contactId: string,
  top?: number
): Promise<ProgenyPlanningObjective[]> {
  const query = new URLSearchParams({ contact_id: contactId });
  if (top != null) query.set("top", String(top));
  return get<ProgenyPlanningObjective[]>(`${PROGENY_BASE_URL}/planning-objectives/by-contact?${query}`);
}

/**
 * List financial plans from Progeny for a given account
 */
export async function getPlansByAccount(
  accountId: string,
  top?: number
): Promise<ProgenyPlan[]> {
  const query = new URLSearchParams({ account_id: accountId });
  if (top != null) query.set("top", String(top));
  return get<ProgenyPlan[]>(`${PROGENY_BASE_URL}/plans/by-account?${query}`);
}

/**
 * List recommendations from Progeny for a given account
 */
export async function getRecommendationsByAccount(
  accountId: string,
  top?: number
): Promise<ProgenyRecommendation[]> {
  const query = new URLSearchParams({ account_id: accountId });
  if (top != null) query.set("top", String(top));
  return get<ProgenyRecommendation[]>(
    `${PROGENY_BASE_URL}/recommendations/by-account?${query}`
  );
}

/**
 * List attitude-to-risk assessments from Progeny for a given account
 */
export async function getAttitudeToRiskByAccount(
  accountId: string,
  top?: number
): Promise<ProgenyAttitudeToRisk[]> {
  const query = new URLSearchParams({ account_id: accountId });
  if (top != null) query.set("top", String(top));
  return get<ProgenyAttitudeToRisk[]>(
    `${PROGENY_BASE_URL}/attitude-to-risk/by-account?${query}`
  );
}

/**
 * List vulnerabilities from Progeny for a given account
 */
export async function getVulnerabilitiesByAccount(
  accountId: string
): Promise<ProgenyVulnerability[]> {
  const query = new URLSearchParams({ account_id: accountId });
  return get<ProgenyVulnerability[]>(
    `${PROGENY_BASE_URL}/vulnerabilities/by-account?${query}`
  );
}

/**
 * List planning objectives from Progeny for a given account
 */
export async function getPlanningObjectivesByAccount(
  accountId: string,
  top?: number
): Promise<ProgenyPlanningObjective[]> {
  const query = new URLSearchParams({ account_id: accountId });
  if (top != null) query.set("top", String(top));
  return get<ProgenyPlanningObjective[]>(
    `${PROGENY_BASE_URL}/planning-objectives/by-account?${query}`
  );
}

/**
 * Classify documents using the document taxonomy module.
 * Returns documents with classification metadata including category,
 * compliance status, and naming validation.
 */
export async function classifyDocuments(
  documents: ProgenyDocument[]
): Promise<ClassifyDocumentsResponse> {
  return post<ClassifyDocumentsResponse>(`${PROGENY_BASE_URL}/documents/classify`, {
    documents,
  });
}

/**
 * Validate a document filename against naming conventions.
 * Returns parsed components and validation warnings/errors.
 */
export async function validateDocumentNaming(
  filename: string
): Promise<NamingValidation> {
  return get<NamingValidation>(
    `${PROGENY_BASE_URL}/documents/validate-naming?filename=${encodeURIComponent(filename)}`
  );
}

// ============================================================================
// Admin Debug API Functions
// ============================================================================

/**
 * Debug: fetch raw contact JSON from Progeny via admin endpoint
 */
export async function getAdminContact(contactId: string): Promise<ProgenyContact> {
  return get<ProgenyContact>(`${PROGENY_BASE_URL}/contact/${contactId}`);
}

/**
 * Debug: list raw documents from Progeny for a contact via admin endpoint
 */
export async function getAdminDocumentsByContact(
  contactId: string
): Promise<AdminDocumentsDebugResponse> {
  return get<AdminDocumentsDebugResponse>(
    `${PROGENY_BASE_URL}/documents/by-contact/${contactId}`
  );
}

export const progenyAPI = {
  // Admin
  getStatus,
  getConfig,
  getStats,
  updateConfig,
  testConnection,
  reAuth,
  getProgenyEnvironment,
  setProgenyEnvironment,
  listCaseAccountReviews,
  discoverCaseDocuments,
  getWebhooks,
  getWebhookById,
  getProcessingLogs,
  getProcessingLogById,
  triggerReview,
  queueAdminProgenyChecksTrigger,
  getAdminReviewContext,
  getReviewDocuments,
  getAdminContact,
  getAdminDocumentsByContact,
  // User-facing
  getContact,
  getAccount,
  getDocumentsByContact,
  getDocumentsByAccount,
  syncDocuments,
  getReviewContext,
  getReviewDetail,
  getFullReview,
  getReviewsByContact,
  getReviewsByAccount,
  getHouseholdFactfind,
  getTrustFactfind,
  getCorporateFactfind,
  getCorporateFactfindsByAccount,
  getMeetingNotes,
  getMeetingNoteDocument,
  getSaturnMeetingNoteDocumentUrl,
  downloadSaturnMeetingNoteDocument,
  getLatestClientMeetingNote,
  getMeetingTranscript,
  searchSmartSearchSubjects,
  getPlansByContact,
  getPlansByAccount,
  getRecommendationsByContact,
  getRecommendationsByAccount,
  getAttitudeToRiskByContact,
  getAttitudeToRiskByAccount,
  getVulnerabilitiesByContact,
  getVulnerabilitiesByAccount,
  getPlanningObjectivesByContact,
  getPlanningObjectivesByAccount,
  // Document classification
  classifyDocuments,
  validateDocumentNaming,
};
